import React, { useState } from 'react';
import { Typography, Box, createTheme, ThemeProvider, Stack } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ISwitchWithCustomText } from '../Switch/SwitchWithCustomText';
import { IUserConfiguration } from '../../redux/configuration/types';
import { IPaymentCard } from '../../types/global/generic.types';
import PaymentCard from '../PaymentCard/PaymentCard';
import { userConfigurationSelector } from '../../selectors/selectors';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import CustomButton from '../Button/CustomButton';
import AddIcon from '../CustomSVGIcons/AddIcon';
import PaymentModal from '../Payment/PaymentModal';
import { strings } from '../../constants';
import { PayStack } from '../Payment/Paystack';
import { getJWtDetails } from '../../utils/AppUtils';
import { DialogueSheet } from '../DialogueSheet/DialogueSheet';
import { en_config } from '../../config';

export interface ISettingsProps extends Omit<ISwitchWithCustomText, 'header' | 'body' | 'theme'> {
    onChange?: (payload: any) => void;
    setting?: IUserConfiguration;
    paymentMethods?: IPaymentCard[];
    onDeleteCard?: (card: IPaymentCard, isDefaultCard?: boolean) => void;
    onMakeDefault?: (card: IPaymentCard) => void;
    isNonUs?: boolean;
    publicKey: string;
}

export function isExpired(month: string, year: string) {
    // Create a Day.js object for the input date (end of the specified month)
    const inputDate = dayjs(`${year}-${month}-01`).endOf('month');

    // Get the current date
    const currentDate = dayjs();

    // Compare the two dates
    return inputDate.isBefore(currentDate);
}

const AccountBilling: React.FC<ISettingsProps> = ({
    paymentMethods,
    onDeleteCard,
    onMakeDefault,
    isNonUs,
    publicKey,
}) => {
    const { themeStyle } = useThemeContext();
    const userSpecificConfiguration = useSelector(userConfigurationSelector);
    const [showDeleteDialogue, setShowDeleteDialogue] = useState(false);
    const [cardToDelete, setCardToDelete] = useState<IPaymentCard | null>(null);
    const [open, setOpen] = useState(false);
    const { user } = getJWtDetails();

    const switchTheme = createTheme({
        components: {
            MuiSwitch: {
                styleOverrides: {
                    switchBase: {
                        // Controls default (unchecked) color for the thumb
                        color: theme[themeStyle].tag_white,
                    },
                    colorPrimary: {
                        '&.Mui-checked': {
                            // Controls checked color for the thumb
                            color: theme[themeStyle].tag_white,
                        },
                    },
                    track: {
                        // Controls default (unchecked) color for the track
                        opacity: 1,
                        backgroundColor: theme[themeStyle].gray_message,
                        '.Mui-checked.Mui-checked + &': {
                            // Controls checked color for the track
                            opacity: 0.9,
                            backgroundColor: theme[themeStyle].primary_orange,
                        },
                    },
                },
            },
            //@ts-ignore
            custom: {},
        },
    });

    return (
        <ThemeProvider theme={switchTheme}>
            <PaymentModal onClose={() => setOpen(!open)} open={open} amount={10} />
            <Box
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                }}
                sx={{
                    gap: '10px',
                    width: '100%',
                    height: '100%',
                    p: {
                        xs: 2,
                        sm: 0,
                    },
                }}>
                {cardToDelete && (
                    <DialogueSheet
                        onClose={() => {
                            setCardToDelete(null);
                            setShowDeleteDialogue(false);
                        }}
                        open={showDeleteDialogue}
                        onAgreeClicked={() => {
                            onDeleteCard &&
                                onDeleteCard(
                                    cardToDelete,
                                    userSpecificConfiguration?.default_payment_method === cardToDelete._id,
                                );
                            setShowDeleteDialogue(false);
                        }}
                        onDisagreeClick={() => {
                            setCardToDelete(null);
                            setShowDeleteDialogue(false);
                        }}
                        header="Delete Card?"
                        body="You are about to delete this card and this action cannot be reversed."
                    />
                )}
                <Stack direction="row" alignItems="center" justifyContent="space-between" width={'100%'}>
                    <Typography
                        style={{
                            fontSize: 36,
                            fontWeight: '600',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            textAlign: 'left',
                            color: theme[themeStyle].filters_white,
                        }}>
                        Billing
                    </Typography>
                    {isNonUs && user ? (
                        <PayStack
                            email={user.email}
                            amount={en_config.PAYSTACK_CHARGE} // Charge N50 to authenticate
                            publicKey={publicKey}
                            isAddNewCardButton={false}
                        />
                    ) : (
                        <CustomButton
                            label={strings.add_new_card}
                            onClick={() => setOpen(!open)}
                            withIcon
                            customIcon={
                                <AddIcon
                                    style={{ marginLeft: '4px' }}
                                    height={'20px'}
                                    width={'20px'}
                                    fill={theme[themeStyle].zeus_dark}
                                />
                            }
                        />
                    )}
                </Stack>
                {paymentMethods?.map(
                    (card, index) =>
                        card.authorization && (
                            <PaymentCard
                                key={index}
                                card={card}
                                isExpired={isExpired(card.authorization?.exp_month, card.authorization?.exp_year)}
                                isDefault={userSpecificConfiguration?.default_payment_method === card._id}
                                onSetDefault={() => onMakeDefault && onMakeDefault(card)}
                                onDelete={() => {
                                    setCardToDelete(card);
                                    setShowDeleteDialogue(!showDeleteDialogue);
                                }}
                            />
                        ),
                )}
                {isNonUs && (
                    <Typography
                        style={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 8, color: theme[themeStyle].tag_white }}>
                        When adding a new card, we will initiate a small N{en_config.PAYSTACK_CHARGE / 100} charge to
                        authenticate your account. This will be refunded.
                    </Typography>
                )}
            </Box>
        </ThemeProvider>
    );
};

export { AccountBilling };
